import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { Model3DElement } from '../types';

/**
 * 3D模型加载器
 * 用于在Canvas3D中加载和渲染3D家具模型
 */

// 模型缓存
const modelCache = new Map<string, THREE.Object3D>();

type ModelFormat = 'obj' | 'glb' | 'gltf' | 'fbx';

const DATA_MIME_FORMATS: Record<string, ModelFormat> = {
  'model/obj': 'obj',
  'model/gltf-binary': 'glb',
  'model/gltf+json': 'gltf',
  'application/octet-stream': 'fbx',
};

export function detectModelFormat(modelPath: string): ModelFormat | undefined {
  if (modelPath.startsWith('data:')) {
    const mimeType = /^data:([^;,]+)/i.exec(modelPath)?.[1]?.toLowerCase();
    return mimeType ? DATA_MIME_FORMATS[mimeType] : undefined;
  }

  const cleanPath = modelPath.split(/[?#]/, 1)[0];
  const extension = cleanPath.slice(cleanPath.lastIndexOf('.') + 1).toLowerCase();
  return ['obj', 'glb', 'gltf', 'fbx'].includes(extension)
    ? extension as ModelFormat
    : undefined;
}

// 清理所有缓存的模型
export function clearModelCache(): void {
  modelCache.clear();
  console.log('🧹 3D模型缓存已清理');
}

/**
 * 深度克隆3D对象，包括材质
 * Three.js的clone()方法默认不会克隆材质，导致多个实例共享同一材质
 * 这会导致修改一个实例的颜色影响所有实例
 */
function deepCloneObject3D(source: THREE.Object3D): THREE.Object3D {
  const cloned = source.clone();
  
  // 遍历所有子对象，克隆材质
  cloned.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      // 克隆材质
      if (Array.isArray(child.material)) {
        child.material = child.material.map((mat) => mat.clone());
      } else if (child.material) {
        child.material = child.material.clone();
      }
      
      // 几何体通常可以共享（不可变），但如果需要也可以克隆
      // child.geometry = child.geometry.clone();
    }
  });
  
  return cloned;
}

/**
 * 加载3D模型文件
 */
export async function load3DModel(modelPath: string): Promise<THREE.Object3D | null> {
  // 检查缓存
  if (modelCache.has(modelPath)) {
    console.log(`📦 从缓存加载模型: ${modelPath}`);
    // 使用深度克隆，确保材质独立
    return deepCloneObject3D(modelCache.get(modelPath)!);
  }

  console.log(`📥 开始加载模型: ${modelPath}`);

  try {
    const ext = detectModelFormat(modelPath);
    let object: THREE.Object3D | null = null;

    if (ext === 'obj') {
      const loader = new OBJLoader();
      object = await new Promise<THREE.Group>((resolve, reject) => {
        loader.load(
          modelPath,
          (obj: THREE.Group) => {
            console.log(`✅ OBJ模型加载成功: ${modelPath}`);
            resolve(obj);
          },
          (progress) => {
            if (progress.lengthComputable) {
              const percent = ((progress.loaded / progress.total) * 100).toFixed(1);
              console.log(`⏳ 加载进度: ${percent}%`);
            }
          },
          (error: unknown) => {
            console.error(`❌ OBJ模型加载失败: ${modelPath}`, error);
            reject(error);
          }
        );
      });
    } else if (ext === 'gltf' || ext === 'glb') {
      const loader = new GLTFLoader();
      const gltf = await new Promise<any>((resolve, reject) => {
        loader.load(
          modelPath,
          (gltf: any) => {
            console.log(`✅ GLTF/GLB模型加载成功: ${modelPath}`);
            resolve(gltf);
          },
          (progress) => {
            if (progress.lengthComputable) {
              const percent = ((progress.loaded / progress.total) * 100).toFixed(1);
              console.log(`⏳ 加载进度: ${percent}%`);
            }
          },
          (error: unknown) => {
            console.error(`❌ GLTF/GLB模型加载失败: ${modelPath}`, error);
            reject(error);
          }
        );
      });
      object = gltf.scene;
    } else if (ext === 'fbx') {
      const loader = new FBXLoader();
      object = await new Promise<THREE.Group>((resolve, reject) => {
        loader.load(
          modelPath,
          (fbx: THREE.Group) => {
            console.log(`✅ FBX模型加载成功: ${modelPath}`);
            // FBX模型可能包含动画，需要特殊处理
            // 移除动画混合器以避免性能问题
            fbx.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                // 确保材质正确
                if (!child.material) {
                  child.material = new THREE.MeshStandardMaterial({ color: 0x888888 });
                }
              }
            });
            resolve(fbx);
          },
          (progress) => {
            if (progress.lengthComputable) {
              const percent = ((progress.loaded / progress.total) * 100).toFixed(1);
              console.log(`⏳ 加载进度: ${percent}%`);
            }
          },
          (error: unknown) => {
            console.error(`❌ FBX模型加载失败: ${modelPath}`, error);
            reject(error);
          }
        );
      });
    } else {
      console.warn(`⚠️ 不支持的模型格式: ${ext}`);
      return null;
    }

    if (object) {
      // 检查模型是否有效
      let vertexCount = 0;
      object.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          vertexCount += child.geometry.attributes.position?.count || 0;
        }
      });
      
      console.log(`📊 模型统计: ${vertexCount} 个顶点`);
      
      if (vertexCount === 0) {
        console.warn(`⚠️ 模型没有几何体数据: ${modelPath}`);
        return null;
      }

      // 缓存模型
      modelCache.set(modelPath, object);
      // 使用深度克隆，确保材质独立
      return deepCloneObject3D(object);
    }

    return null;
  } catch (error) {
    console.error(`❌ 加载3D模型失败: ${modelPath}`, error);
    return null;
  }
}

/**
 * 创建3D家具模型的Three.js对象
 * 
 * 层级系统说明：
 * - 3D家具使用固定的最高层级 (renderOrder = 3000)，确保始终在所有区域之上
 * - Y轴轻微偏移：每个家具增加 0.001m + ID哈希偏移，用于避免家具自身的深度冲突
 * - 家具的实际高度由模型自身决定（离地高度由 elevation 控制）
 */
export async function create3DFurnitureMesh(model: Model3DElement): Promise<THREE.Group> {
  const group = new THREE.Group();
  group.name = model.id;
  group.userData = { type: 'model3d', elementId: model.id };

  // 计算家具的Y轴偏移量（仅用于避免家具自身的深度冲突）
  // 层级偏移：0.001m（1mm），确保不同家具不会完全在同一深度
  // ID哈希偏移：0~0.0005m，确保同一位置的不同家具也有微小差异
  const levelYOffset = 0.001;
  const idHash = model.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const idYOffset = (idHash % 50) / 100000; // 0 ~ 0.0005m
  const totalYOffset = levelYOffset + idYOffset;

  console.log(`🎯 [create3DFurnitureMesh] ==================== 开始处理 3D 模型 ====================`);
  console.log(`🎯 [create3DFurnitureMesh] 模型名称: ${model.modelName}`);
  console.log(`🎯 [create3DFurnitureMesh] 元素ID: ${model.id}`);
  console.log(`🎯 [create3DFurnitureMesh] Y偏移: ${totalYOffset.toFixed(6)}m (用于避免家具自身深度冲突)`);
  console.log(`🎯 [create3DFurnitureMesh] renderOrder: 3000 (确保在所有区域之上)`);

  // 坐标系统转换（与设计文档一致）
  // ==========================================
  // 2D Canvas坐标系：X轴向右，Y轴向下（像素单位）
  // 3D World坐标系：X轴向右，Y轴向上，Z轴向前（米单位）
  //
  // 坐标映射规则：
  // - 2D X → 3D X（保持方向）
  // - 2D Y → 3D Z（Y轴向下映射到Z轴向前）
  //
  // 单位转换：
  // - 像素 ÷ 100 = 米（一致的转换因子）
  // - 毫米 ÷ 1000 = 米
  //
  // 校准比例的应用：
  // - 2D canvas尺寸已经根据calibrationScale计算（在FurnitureLibrary中）
  // - 这里只需要应用统一的像素到米转换（÷100）
  // ==========================================
  
  // 计算模型中心点（2D坐标，单位：像素）
  const centerX = model.points.reduce((sum, p) => sum + p.x, 0) / model.points.length;
  const centerY = model.points.reduce((sum, p) => sum + p.y, 0) / model.points.length;
  
  const posX = centerX / 100; // 像素 → 米
  const posZ = centerY / 100; // 像素 → 米（Y轴映射到Z轴）

  console.log(`📍 模型 "${model.modelName}" 3D位置:`, {
    canvasPos: { x: centerX.toFixed(1), y: centerY.toFixed(1), unit: 'px' },
    worldPos: { x: posX.toFixed(3), y: 0, z: posZ.toFixed(3), unit: 'm' }
  });

  // 加载3D模型
  const modelObject = await load3DModel(model.modelPath);

  console.log(`🧪 [${model.modelName}] load3DModel 返回值: ${modelObject ? 'Object' : 'null/undefined'}`);
  console.log(`🧪 [${model.modelName}] modelObject 类型: ${modelObject?.constructor?.name}`);
  console.trace(`🧪 [${model.modelName}] TRACE: load3DModel 返回后`);

  if (modelObject) {
    // 计算模型的边界框
    const box = new THREE.Box3().setFromObject(modelObject);
    const size = new THREE.Vector3();
    box.getSize(size);
    
    console.log(`📦 模型 "${model.modelName}" 原始边界框:`, {
      width: size.x.toFixed(3),
      height: size.y.toFixed(3),
      depth: size.z.toFixed(3),
      unit: '模型单位',
      min: { x: box.min.x.toFixed(3), y: box.min.y.toFixed(3), z: box.min.z.toFixed(3) },
      max: { x: box.max.x.toFixed(3), y: box.max.y.toFixed(3), z: box.max.z.toFixed(3) }
    });

    // 应用颜色到所有材质
    const modelColor = new THREE.Color(model.color || '#888888');
    console.log(`🎨 应用颜色到模型 "${model.modelName}": ${model.color || '#888888'}`);
    
    modelObject.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // 如果材质是数组，遍历所有材质
        if (Array.isArray(child.material)) {
          child.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhongMaterial) {
              mat.color.copy(modelColor);
              // 家具应该完全不透明，确保正确渲染
              mat.depthWrite = true;
            }
          });
        } else if (child.material instanceof THREE.MeshStandardMaterial || child.material instanceof THREE.MeshPhongMaterial) {
          child.material.color.copy(modelColor);
          // 家具应该完全不透明，确保正确渲染
          child.material.depthWrite = true;
        }
      }
    });

    // 计算目标尺寸
    // 注意：model.width、model.depth 和 model.height 都是厘米单位（来自 models.json）
    // 需要除以100转换为3D世界坐标（米）
    const targetWidth = model.width / 100;   // 厘米 → 米
    const targetDepth = model.depth / 100;   // 厘米 → 米
    const targetHeight = model.height / 100; // 厘米 → 米

    // 计算每个维度的缩放因子
    // 假设模型的X对应宽度，Z对应深度，Y对应高度
    const scaleX = size.x > 0 ? targetWidth / size.x : 1;
    const scaleY = size.y > 0 ? targetHeight / size.y : 1;
    const scaleZ = size.z > 0 ? targetDepth / size.z : 1;

    // 使用统一缩放以保持模型比例（基于宽度和深度的平均值）
    // 高度使用独立缩放以匹配真实高度
    const baseScale = (scaleX + scaleZ) / 2;

    console.log(`🔧 [${model.modelName}] ==================== 缩放计算开始 ====================`);
    console.log(`🔧 [${model.modelName}] Canvas 2D 尺寸: width=${model.width.toFixed(2)}px, depth=${model.depth.toFixed(2)}px`);
    console.log(`🔧 [${model.modelName}] 目标 3D 尺寸: width=${targetWidth.toFixed(4)}m, height=${targetHeight.toFixed(4)}m, depth=${targetDepth.toFixed(4)}m`);
    console.log(`🔧 [${model.modelName}] 原始模型尺寸: width=${size.x.toFixed(4)}, height=${size.y.toFixed(4)}, depth=${size.z.toFixed(4)}`);
    console.log(`🔧 [${model.modelName}] 缩放因子: scaleX=${scaleX.toFixed(4)}, scaleY=${scaleY.toFixed(4)}, scaleZ=${scaleZ.toFixed(4)}`);
    console.log(`🔧 [${model.modelName}] 基础缩放: baseScale=${baseScale.toFixed(4)}, 用户缩放: userScale=${model.scale || 1}`);

    // 计算缩放前的原始边界框中心偏移
    // 这是关键：原始模型可能不在原点，缩放时需要保持中心位置不变
    const originalCenter = new THREE.Vector3();
    box.getCenter(originalCenter);

    console.log(`📐 [${model.modelName}] 原始边界框: min=(${box.min.x.toFixed(4)}, ${box.min.y.toFixed(4)}, ${box.min.z.toFixed(4)}), max=(${box.max.x.toFixed(4)}, ${box.max.y.toFixed(4)}, ${box.max.z.toFixed(4)})`);
    console.log(`📐 [${model.modelName}] 原始中心点: (${originalCenter.x.toFixed(4)}, ${originalCenter.y.toFixed(4)}, ${originalCenter.z.toFixed(4)})`);

    // 应用基础缩放和用户缩放
    // 关键修复：使用单独的 scaleX 和 scaleZ，而不是平均值
    // 这样可以确保宽度和深度分别匹配目标尺寸
    const finalScaleX = scaleX * (model.scale || 1);
    const finalScaleZ = scaleZ * (model.scale || 1);

    console.log(`🎯 [${model.modelName}] 分别缩放: finalScaleX=${finalScaleX.toFixed(4)}, finalScaleZ=${finalScaleZ.toFixed(4)}`);

    // 分别设置每个轴的缩放
    modelObject.scale.set(finalScaleX, finalScaleX, finalScaleZ);

    // 重新计算边界框以验证
    const scaledBox = new THREE.Box3().setFromObject(modelObject);
    const scaledSize = new THREE.Vector3();
    scaledBox.getSize(scaledSize);

    console.log(`✅ [${model.modelName}] 缩放后边界框: min=(${scaledBox.min.x.toFixed(4)}, ${scaledBox.min.y.toFixed(4)}, ${scaledBox.min.z.toFixed(4)}), max=(${scaledBox.max.x.toFixed(4)}, ${scaledBox.max.y.toFixed(4)}, ${scaledBox.max.z.toFixed(4)})`);
    console.log(`✅ [${model.modelName}] 缩放后尺寸: width=${scaledSize.x.toFixed(4)}m, height=${scaledSize.y.toFixed(4)}m, depth=${scaledSize.z.toFixed(4)}m`);

    // 计算缩放后的边界框中心
    const scaledCenter = new THREE.Vector3();
    scaledBox.getCenter(scaledCenter);
    console.log(`✅ [${model.modelName}] 缩放后中心点: (${scaledCenter.x.toFixed(4)}, ${scaledCenter.y.toFixed(4)}, ${scaledCenter.z.toFixed(4)})`);

    // =====================================================
    // 关键修复：使用 modelObject.position 来补偿几何体中心偏移
    // 这样旋转会围绕正确的中心进行，而不是围绕模型原点
    // =====================================================
    console.log(`🔧 [${model.modelName}] 设置 modelObject.position 来补偿中心偏移: (${-scaledCenter.x.toFixed(4)}, ?, ${-scaledCenter.z.toFixed(4)})`);

    // 计算离地高度（cm → 米）
    const elevation = (model.elevation || 0) / 100;

    // 计算缩放后边界框的底部位置
    const scaledBottomY = scaledBox.min.y;

    // modelObject.position.y = elevation - scaledBottomY
    // 这样模型的底部会对齐到 elevation 高度
    modelObject.position.set(
      -scaledCenter.x,
      elevation - scaledBottomY, // Y 轴：底部对齐到 elevation
      -scaledCenter.z
    );

    console.log(`🔧 [${model.modelName}] modelObject.position: (${modelObject.position.x.toFixed(4)}, ${modelObject.position.y.toFixed(4)}, ${modelObject.position.z.toFixed(4)})`);
    console.log(`🔧 [${model.modelName}] elevation: ${elevation.toFixed(4)}m`);
    console.log(`🔧 [${model.modelName}] scaledBottomY: ${scaledBottomY.toFixed(4)}`);

    // 验证：缩放后的边界框中心应该接近原点
    const recenteredBox = new THREE.Box3().setFromObject(modelObject);
    const recenteredCenter = new THREE.Vector3();
    recenteredBox.getCenter(recenteredCenter);
    console.log(`✅ [${model.modelName}] recenteredBox 中心: (${recenteredCenter.x.toFixed(6)}, ${recenteredCenter.y.toFixed(6)}, ${recenteredCenter.z.toFixed(6)})`);

    console.log(`🎯 [${model.modelName}] ==================== 位置计算结束 ====================`);

    // 添加到组（先不添加，等设置好位置后再添加）
    group.add(modelObject);

    // 应用旋转到 group，而不是 modelObject
    // 这样 modelObject.position 负责居中，group.position 负责世界位置和旋转
    // 旋转会围绕 group 的原点（即 2D 中心点）进行
    if (model.rotation) {
      group.rotation.y = (model.rotation * Math.PI) / 180;
      console.log(`🔄 [${model.modelName}] 应用 group.rotation.y = ${model.rotation}° = ${(model.rotation * Math.PI / 180).toFixed(4)} rad`);
    }
    if (model.rotationX) {
      group.rotation.x = (model.rotationX * Math.PI) / 180;
      console.log(`🔄 [${model.modelName}] 应用 group.rotation.x = ${model.rotationX}° = ${(model.rotationX * Math.PI / 180).toFixed(4)} rad`);
    }

    console.log(`🔄 [${model.modelName}] 旋转后 group.rotation: (${group.rotation.x.toFixed(4)}, ${group.rotation.y.toFixed(4)}, ${group.rotation.z.toFixed(4)}) rad`);
    console.log(`🔄 [${model.modelName}] modelObject.position: (${modelObject.position.x.toFixed(4)}, ${modelObject.position.y.toFixed(4)}, ${modelObject.position.z.toFixed(4)})`);
  } else {
    // 如果模型加载失败，创建一个简单的占位符（立方体）
    console.warn(`⚠️ 模型加载失败，使用占位符: ${model.modelPath}`);
    const geometry = new THREE.BoxGeometry(
      model.width / 100,   // 厘米 → 米
      model.height / 100,  // 厘米 → 米
      model.depth / 100    // 厘米 → 米
    );
    const material = new THREE.MeshStandardMaterial({
      color: model.color || '#93c5fd',
      roughness: 0.7,
      metalness: 0.1,
      // 家具应该完全不透明
      depthWrite: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = model.height / 200; // 抬高到地面上方（厘米 → 米，除以2得到中心高度）
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  // 设置3D场景中的位置（X和Z轴）
  console.log(`📍 [${model.modelName}] ==================== 设置3D位置 ====================`);
  console.log(`📍 [${model.modelName}] Canvas 2D 中心点: (${centerX.toFixed(2)}, ${centerY.toFixed(2)})px`);
  console.log(`📍 [${model.modelName}] 转换为3D坐标: X=${(centerX / 100).toFixed(4)}m, Z=${(centerY / 100).toFixed(4)}m`);

  // 设置组的位置（仅X和Z，Y由modelObject自身根据elevation控制，确保家具正确落地）
  // Y位置由 modelObject.position.y 控制（elevation - scaledBottomY），确保底部对齐到elevation高度
  group.position.set(posX, 0, posZ);
  
  // 设置渲染顺序，使用固定最高层级 3000
  // 确保3D家具始终在所有区域之上渲染（不依赖Y偏移）
  group.renderOrder = 3000;
  
  console.log(`📍 [${model.modelName}] 已设置 group.position: (${group.position.x.toFixed(4)}, ${group.position.y.toFixed(6)}, ${group.position.z.toFixed(4)})`);
  console.log(`📍 [${model.modelName}] 已设置 group.renderOrder: ${group.renderOrder} (确保在所有区域之上)`);

  // 验证位置是否正确（防止浮点数精度问题导致的位置偏移）
  const expectedPosX = posX;
  const expectedPosZ = posZ;
  const actualPosX = group.position.x;
  const actualPosZ = group.position.z;

  console.log(`📍 [${model.modelName}] 位置验证: expected=(${expectedPosX.toFixed(6)}, ${expectedPosZ.toFixed(6)}), actual=(${actualPosX.toFixed(6)}, ${actualPosZ.toFixed(6)})`);

  if (Math.abs(actualPosX - expectedPosX) > 0.0001 || Math.abs(actualPosZ - expectedPosZ) > 0.0001) {
    console.warn(`⚠️ [${model.modelName}] XZ平面位置偏移检测到，校正中...`);
    group.position.set(expectedPosX, 0, expectedPosZ);
  } else {
    console.log(`✅ [${model.modelName}] XZ平面位置验证通过`);
  }

  // 打印模型的最终边界框（验证位置是否正确）
  const finalBox = new THREE.Box3().setFromObject(group);
  const finalMin = finalBox.min;
  const finalMax = finalBox.max;
  const finalCenter = new THREE.Vector3();
  finalBox.getCenter(finalCenter);

  console.log(`📐 [${model.modelName}] 最终边界框 (world space):`);
  console.log(`   min: (${finalMin.x.toFixed(4)}, ${finalMin.y.toFixed(4)}, ${finalMin.z.toFixed(4)})`);
  console.log(`   max: (${finalMax.x.toFixed(4)}, ${finalMax.y.toFixed(4)}, ${finalMax.z.toFixed(4)})`);
  console.log(`   center: (${finalCenter.x.toFixed(4)}, ${finalCenter.y.toFixed(4)}, ${finalCenter.z.toFixed(4)})`);
  console.log(`   group.position: (${group.position.x.toFixed(4)}, ${group.position.y.toFixed(4)}, ${group.position.z.toFixed(4)})`);

  // 计算边界框中心与 group.position 的差值
  const centerDiffX = Math.abs(finalCenter.x - group.position.x);
  const centerDiffZ = Math.abs(finalCenter.z - group.position.z);
  console.log(`📐 [${model.modelName}] 边界框中心与 group.position 差值: X=${centerDiffX.toFixed(6)}, Z=${centerDiffZ.toFixed(6)}`);

  if (centerDiffX > 0.01 || centerDiffZ > 0.01) {
    console.warn(`⚠️ [${model.modelName}] 模型边界框中心与 group.position 偏差过大！`);
  }

  console.log(`✅ [${model.modelName}] ==================== 3D对象创建完成 ====================`);
  console.log(`✅ [${model.modelName}] 最终位置: group=(${group.position.x.toFixed(4)}, ${group.position.y.toFixed(4)}, ${group.position.z.toFixed(4)})`);
  if (modelObject) {
    console.log(`✅ [${model.modelName}] 最终缩放: (${modelObject.scale.x.toFixed(4)}, ${modelObject.scale.y.toFixed(4)}, ${modelObject.scale.z.toFixed(4)})`);
  }

  return group;
}

/**
 * 批量创建所有3D家具模型
 * 
 * 层级系统说明：
 * - 所有3D家具使用固定的最高层级 (3000)
 * - 确保始终在所有区域之上，避免深度冲突闪烁
 */
export async function createAll3DFurnitureMeshes(models: Model3DElement[]): Promise<THREE.Group> {
  const group = new THREE.Group();
  group.name = 'furnitures';

  console.log(`🪑 [createAll3DFurnitureMeshes] 开始加载 ${models.length} 个3D家具模型（层级: 3000，确保在所有区域之上）`);

  // 并行加载所有模型
  const promises = models.map(model => create3DFurnitureMesh(model));
  const meshes = await Promise.all(promises);

  meshes.forEach(mesh => {
    group.add(mesh);
  });

  console.log(`✅ 加载了 ${meshes.length} 个3D家具模型（层级: 3000，始终在所有区域之上）`);

  return group;
}
